import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, ChevronDown, Search, X, Flame, Droplets, BarChart3 } from 'lucide-react';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { getListedCfdInstrument, matchesCfdSearch } from '../../supabase/functions/_shared/cfdInstruments';
import { CRYPTO_ICONS, CURRENCY_FLAGS, STOCK_LOGOS, INDEX_LOGOS, COMMODITY_IMAGES, getAssetSymbol, getForexPairCurrencies } from '../constants/assetIcons';
import { getStockLogoUrl, getIndexLogoUrl } from '../constants/logoDomains';
import ListedCfdLogo from './ListedCfdLogo';
import { getListedCfdLogo } from '../constants/listedCfdLogos';

interface TradingPairHeaderProps {
  selectedPair: string;
  tradingMode: 'futures' | 'cfd' | 'exchanges';
  onPairChange?: (pair: string) => void;
}

const TradingPairHeader: React.FC<TradingPairHeaderProps> = ({
  selectedPair,
  tradingMode,
  onPairChange
}) => {
  const { getPriceBySymbol, getMarketData, marketData, valoreConnected, lastUpdateTime } = useUnifiedPrices();
  const [fundingCountdown, setFundingCountdown] = useState('');
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const currentPrice = useMemo(() => {
    return getPriceBySymbol(selectedPair);
  }, [getPriceBySymbol, selectedPair, lastUpdateTime, valoreConnected]);

  const pairData = useMemo(() => {
    return getMarketData(selectedPair);
  }, [getMarketData, selectedPair, lastUpdateTime]);

  const priceChange24h = pairData?.change_24h || 0;
  const high24h = pairData?.high_price_24h || currentPrice * 1.02;
  const low24h = pairData?.low_price_24h || currentPrice * 0.98;
  const volume24h = pairData?.volume_24h || 0;

  const indexPrice = currentPrice;
  const fundingRate = 0.01;
  const openInterest = volume24h * currentPrice * 0.1;

  useEffect(() => {
    const calculateFundingCountdown = () => {
      const now = new Date();
      const hours = now.getUTCHours();
      const nextFundingHour = Math.ceil(hours / 8) * 8;
      const targetHour = nextFundingHour >= 24 ? 0 : nextFundingHour;

      const target = new Date(now);
      target.setUTCHours(targetHour, 0, 0, 0);
      if (target <= now) {
        target.setUTCDate(target.getUTCDate() + 1);
      }

      const diff = target.getTime() - now.getTime();
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);

      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    setFundingCountdown(calculateFundingCountdown());
    const interval = setInterval(() => {
      setFundingCountdown(calculateFundingCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPairSelector(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showPairSelector && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showPairSelector]);

  const allPairs = useMemo(() => {
    if (tradingMode === 'cfd') {
      return CFD_INSTRUMENTS.map(p => ({ ...p }));
    }
    return TOP_CRYPTO_PAIRS.map(p => ({ ...p }));
  }, [tradingMode]);

  const categories = useMemo(() => {
    if (tradingMode === 'cfd') {
      return [
        { key: 'all', label: 'All' },
        { key: 'forex', label: 'Forex' },
        { key: 'commodity', label: 'Commodities' },
        { key: 'stock', label: 'Stocks' },
        { key: 'index', label: 'Indices' },
        { key: 'etf', label: 'ETFs' }
      ];
    }
    return [{ key: 'all', label: 'All Perpetual' }];
  }, [tradingMode]);

  const filteredPairs = useMemo(() => {
    let pairs = [...allPairs];

    pairs = pairs.filter(pair => {
      const price = getPriceBySymbol(pair.symbol);
      return price > 0 || !!getListedCfdInstrument(pair.symbol);
    });

    if (activeCategory !== 'all') {
      pairs = pairs.filter(p => p.type === activeCategory);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      pairs = pairs.filter(pair =>
        matchesCfdSearch(pair, term)
      );
    }

    return pairs;
  }, [allPairs, activeCategory, searchTerm, getPriceBySymbol, lastUpdateTime]);

  const handlePairSelect = (symbol: string) => {
    onPairChange?.(symbol);
    setShowPairSelector(false);
    setSearchTerm('');
    setActiveCategory('all');
  };

  const formatPairDisplay = (symbol: string) => {
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  const formatPrice = (price: number, symbol?: string) => {
    if (price === 0) return '0.00';

    const isForexPair = symbol && (
      symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('AUD') ||
      symbol.includes('NZD') || symbol.includes('CAD') || symbol.includes('CHF') ||
      (symbol.includes('USD') && !symbol.includes('USDT') && !symbol.endsWith('/USD'))
    );
    const isJpyPair = symbol && symbol.includes('JPY');

    let formatted: string;
    if (isJpyPair) {
      formatted = price.toFixed(3);
    } else if (isForexPair) {
      formatted = price.toFixed(6);
    } else if (price >= 0.1 && price < 10) {
      formatted = price.toFixed(5);
    } else if (price >= 10000) {
      formatted = price.toFixed(2);
    } else if (price >= 1000) {
      formatted = price.toFixed(2);
    } else if (price >= 100) {
      formatted = price.toFixed(3);
    } else if (price >= 10) {
      formatted = price.toFixed(4);
    } else if (price >= 1) {
      formatted = price.toFixed(6);
    } else if (price >= 0.0001) {
      formatted = price.toFixed(6);
    } else {
      formatted = price.toFixed(8);
    }

    return parseFloat(formatted).toString();
  };

  const formatVolume = (vol: number) => {
    if (vol >= 1e9) return (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return (vol / 1e6).toFixed(2) + 'M';
    if (vol >= 1e3) return (vol / 1e3).toFixed(2) + 'K';
    return vol.toFixed(2);
  };

  const formatLargeNumber = (num: number) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  const getPairPrice = (symbol: string) => {
    const price = getPriceBySymbol(symbol);
    return price > 0 ? price : 0;
  };

  const getPairChange = (symbol: string) => {
    const data = getMarketData(symbol);
    return data?.change_24h || 0;
  };

  const getPairVolume = (symbol: string) => {
    const data = getMarketData(symbol);
    const price = getPriceBySymbol(symbol);
    return data?.volume_24h ? data.volume_24h * price : 0;
  };

  const baseAsset = selectedPair.replace('USDT', '').replace('/USD', '').replace('USD', '');
  const isPositive = priceChange24h >= 0;

  const AssetIcon: React.FC<{ type: string; symbol: string; size?: 'sm' | 'md' | 'lg' }> = ({ type, symbol, size = 'sm' }) => {
    const sizeClass = size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-7 h-7' : 'w-6 h-6';
    const iconSize = size === 'lg' ? 14 : size === 'md' ? 12 : 10;

    if (getListedCfdLogo(symbol)) {
      return <ListedCfdLogo symbol={symbol} className={sizeClass} />;
    }

    if (type === 'crypto') {
      const baseSymbol = getAssetSymbol(symbol);
      const iconUrl = CRYPTO_ICONS[baseSymbol];
      if (iconUrl) {
        return (
          <img
            src={iconUrl}
            alt={baseSymbol}
            className={`${sizeClass} rounded-full object-cover`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        );
      }
      return (
        <div className={`${sizeClass} rounded-full bg-gradient-to-br from-orange-400 to-yellow-500 flex items-center justify-center text-white font-bold text-xs`}>
          {baseSymbol.charAt(0)}
        </div>
      );
    }

    if (type === 'forex') {
      const currencies = getForexPairCurrencies(symbol);
      if (currencies) {
        const [base, quote] = currencies;
        const baseFlag = CURRENCY_FLAGS[base];
        const quoteFlag = CURRENCY_FLAGS[quote];
        if (baseFlag && quoteFlag) {
          return (
            <div className={`${sizeClass} relative`}>
              <img
                src={baseFlag}
                alt={base}
                className="absolute top-0 left-0 w-5 h-5 rounded-full object-cover border-2 border-white z-10"
                style={{ width: size === 'lg' ? '22px' : size === 'md' ? '20px' : '18px', height: size === 'lg' ? '22px' : size === 'md' ? '20px' : '18px' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <img
                src={quoteFlag}
                alt={quote}
                className="absolute bottom-0 right-0 w-5 h-5 rounded-full object-cover border-2 border-white"
                style={{ width: size === 'lg' ? '22px' : size === 'md' ? '20px' : '18px', height: size === 'lg' ? '22px' : size === 'md' ? '20px' : '18px' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          );
        }
      }
      return (
        <div className={`${sizeClass} rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-xs`}>
          $
        </div>
      );
    }

    if (type === 'stock') {
      const stockSymbol = symbol.replace('/USD', '');
      const stockData = STOCK_LOGOS[stockSymbol];
      const bgColor = stockData?.bg || '#3B82F6';
      const textColor = stockData?.text || '#FFFFFF';
      const fontSize = size === 'lg' ? '11px' : size === 'md' ? '10px' : '9px';

      const logoUrl = getStockLogoUrl(stockSymbol);

      if (logoUrl) {
        return (
          <div className={`${sizeClass} rounded-full overflow-hidden bg-white flex items-center justify-center border border-slate-200`}>
            <img
              src={logoUrl}
              alt={stockSymbol}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.style.backgroundColor = bgColor;
                  parent.style.color = textColor;
                  parent.style.fontSize = fontSize;
                  parent.style.fontWeight = '600';
                  parent.style.border = 'none';
                  parent.textContent = stockSymbol.length <= 3 ? stockSymbol : stockSymbol.substring(0, 3);
                }
              }}
            />
          </div>
        );
      }

      return (
        <div
          className={`${sizeClass} rounded-full flex items-center justify-center font-semibold shadow-sm`}
          style={{ backgroundColor: bgColor, color: textColor, fontSize }}
        >
          {stockSymbol.length <= 3 ? stockSymbol : stockSymbol.substring(0, 3)}
        </div>
      );
    }

    if (type === 'index') {
      const indexSymbol = symbol.replace('/USD', '');
      const indexData = INDEX_LOGOS[indexSymbol];
      const bgColor = indexData?.color || '#4169E1';
      const fontSize = size === 'lg' ? '10px' : size === 'md' ? '9px' : '8px';
      const labelText = indexData?.label || indexSymbol.substring(0, 3);

      const logoUrl = getIndexLogoUrl(indexSymbol);

      if (logoUrl) {
        return (
          <div className={`${sizeClass} rounded-full overflow-hidden bg-white flex items-center justify-center border border-slate-200`}>
            <img
              src={logoUrl}
              alt={indexSymbol}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.style.backgroundColor = bgColor;
                  parent.style.color = '#FFFFFF';
                  parent.style.fontSize = fontSize;
                  parent.style.fontWeight = '700';
                  parent.style.border = 'none';
                  parent.textContent = labelText;
                }
              }}
            />
          </div>
        );
      }

      return (
        <div
          className={`${sizeClass} rounded-full flex items-center justify-center text-white font-bold`}
          style={{ backgroundColor: bgColor, fontSize }}
        >
          {labelText}
        </div>
      );
    }

    if (type === 'commodity') {
      const baseSymbol = symbol.split('/')[0];
      const commodityData = COMMODITY_IMAGES[baseSymbol];

      if (commodityData?.image) {
        return (
          <img
            src={commodityData.image}
            alt={baseSymbol}
            className={`${sizeClass} rounded-full object-cover`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.style.display = 'none';
            }}
          />
        );
      }

      if (baseSymbol === 'NATGAS') {
        return (
          <div className={`${sizeClass} rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center`}>
            <Flame size={iconSize} className="text-white" />
          </div>
        );
      }

      if (baseSymbol === 'WTICO' || baseSymbol === 'BCO') {
        return (
          <div className={`${sizeClass} rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center`}>
            <Droplets size={iconSize} className="text-amber-300" />
          </div>
        );
      }

      return (
        <div className={`${sizeClass} rounded-full bg-gradient-to-br ${commodityData?.gradient || 'from-yellow-400 to-amber-500'} flex items-center justify-center`}>
          <span className="text-yellow-900 font-bold" style={{ fontSize: size === 'lg' ? '11px' : size === 'md' ? '10px' : '9px' }}>
            {commodityData?.symbol || baseSymbol.charAt(0)}
          </span>
        </div>
      );
    }

    return (
      <div className={`${sizeClass} rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white font-bold text-xs`}>
        {symbol.charAt(0)}
      </div>
    );
  };

  const currentPairInfo = allPairs.find(p => p.symbol === selectedPair);

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-2 flex-shrink-0 relative z-50">
      <div className="flex items-center justify-between gap-4 overflow-x-auto hide-scrollbar">
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowPairSelector(!showPairSelector)}
              className="flex items-center gap-2 hover:bg-slate-50 px-2 py-1 rounded-lg transition-colors"
            >
              <AssetIcon type={currentPairInfo?.type || 'crypto'} symbol={selectedPair} size="lg" />
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  <span className="font-bold text-slate-900 text-lg">{formatPairDisplay(selectedPair)}</span>
                  {tradingMode === 'futures' && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">Perpetual</span>
                  )}
                  {tradingMode === 'exchanges' && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">Exchange</span>
                  )}
                  {tradingMode === 'cfd' && (
                    <span className="text-xs bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded font-medium">CFD</span>
                  )}
                </div>
              </div>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${showPairSelector ? 'rotate-180' : ''}`} />
            </button>

            {showPairSelector && (
              <div
                className="fixed left-20 bg-white border border-slate-200 rounded-xl shadow-2xl w-[500px]"
                style={{
                  top: '120px',
                  zIndex: 9999,
                  maxHeight: 'calc(100vh - 140px)'
                }}
              >
                <div className="p-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={`Search ${tradingMode === 'cfd' ? 'instruments' : 'pairs'}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-white text-slate-900 pl-10 pr-10 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {categories.length > 1 && (
                  <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 overflow-x-auto bg-white">
                    {categories.map(cat => (
                      <button
                        key={cat.key}
                        onClick={() => setActiveCategory(cat.key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                          activeCategory === cat.key
                            ? 'bg-blue-500 text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-slate-500 border-b border-slate-100 bg-slate-50">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-3 text-right">Price</div>
                  <div className="col-span-2 text-right">24H %</div>
                  <div className="col-span-2 text-right">Volume</div>
                </div>

                <div className="overflow-y-auto bg-white" style={{ maxHeight: '400px' }}>
                  {filteredPairs.length > 0 ? (
                    filteredPairs.map((pair) => {
                      const price = getPairPrice(pair.symbol);
                      const change = getPairChange(pair.symbol);
                      const volume = getPairVolume(pair.symbol);
                      const isSelected = selectedPair === pair.symbol;
                      const changePositive = change >= 0;

                      return (
                        <button
                          key={pair.symbol}
                          onClick={() => handlePairSelect(pair.symbol)}
                          className={`w-full grid grid-cols-12 gap-2 px-4 py-3 text-sm transition-colors hover:bg-slate-50 border-b border-slate-50 ${
                            isSelected ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="col-span-5 flex items-center gap-2 text-left">
                            <div className="flex-shrink-0">
                              <AssetIcon type={pair.type} symbol={pair.symbol} size="md" />
                            </div>
                            <div className="min-w-0">
                              <div className={`font-medium truncate ${isSelected ? 'text-blue-600' : 'text-slate-900'}`}>
                                {formatPairDisplay(pair.symbol)}
                              </div>
                              <div className="text-xs text-slate-500 truncate">{pair.name}</div>
                            </div>
                          </div>
                          <div className="col-span-3 text-right font-mono text-slate-900 self-center">
                            {price > 0 ? formatPrice(price, pair.symbol) : '--'}
                          </div>
                          <div className={`col-span-2 text-right font-mono font-medium self-center ${changePositive ? 'text-green-500' : 'text-red-500'}`}>
                            {changePositive ? '+' : ''}{change.toFixed(2)}%
                          </div>
                          <div className="col-span-2 text-right font-mono text-slate-600 text-xs self-center">
                            {formatVolume(volume)}
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-slate-500">
                      <Search size={32} className="mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No {tradingMode === 'cfd' ? 'instruments' : 'pairs'} found</p>
                      <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>

                <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {filteredPairs.length} {tradingMode === 'cfd' ? 'instruments' : 'pairs'}
                  </span>
                  <button
                    onClick={() => setShowPairSelector(false)}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <span className={`text-2xl font-bold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
              {formatPrice(currentPrice, selectedPair)}
            </span>
            <span className="text-xs text-slate-500">{getListedCfdInstrument(selectedPair)?.type === 'index' ? `${formatPrice(currentPrice, selectedPair)} pts` : `$${formatPrice(currentPrice, selectedPair)}`}</span>
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">Index Price</span>
            <span className="text-slate-700 font-mono">{formatPrice(indexPrice, selectedPair)}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">24h Change</span>
            <span className={`font-mono font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
              {isPositive ? '+' : ''}{priceChange24h.toFixed(2)}%
            </span>
          </div>

          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">24h High</span>
            <span className="text-slate-700 font-mono">{formatPrice(high24h, selectedPair)}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">24h Low</span>
            <span className="text-slate-700 font-mono">{formatPrice(low24h, selectedPair)}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">24h Turnover(USDT)</span>
            <span className="text-slate-700 font-mono">{formatLargeNumber(volume24h * currentPrice)}</span>
          </div>

          <div className="flex flex-col">
            <span className="text-slate-400 text-xs">Open Interest(USDT)</span>
            <span className="text-slate-700 font-mono">{formatLargeNumber(openInterest)}</span>
          </div>
        </div>

        {tradingMode === 'futures' && (
          <div className="flex items-center gap-4 flex-shrink-0 border-l border-slate-200 pl-4">
            <div className="flex flex-col">
              <span className="text-slate-400 text-xs">Funding Rate</span>
              <span className={`font-mono font-medium ${fundingRate >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {fundingRate >= 0 ? '+' : ''}{fundingRate.toFixed(4)}%
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-slate-400 text-xs">Countdown</span>
              <div className="flex items-center gap-1">
                <Clock size={12} className="text-slate-400" />
                <span className="text-slate-700 font-mono">{fundingCountdown}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingPairHeader;
